import React, { useState, useEffect, useRef } from 'react';
import { Clock, Search, Calendar, Users, Filter, PlusCircle, Edit, Trash2, X, Play, Pause, ChevronDown, Briefcase } from 'lucide-react';
import { createTimeEntry, getTimeEntries, deleteTimeEntry, updateTimeEntry } from '../services/timeTrackingService';
import { getAccounts } from '../services/firestoreService';
import { getInternalUsers } from '../services/userService';
import { useTheme } from '../contexts/ThemeContext';
import PortalBackground from '../components/PortalBackground';

const TimeTracking = () => {
  const { darkMode } = useTheme();
  // State for time tracking
  const [clockedIn, setClockedIn] = useState(() => {
    const saved = localStorage.getItem('clockedIn');
    return saved === 'true';
  });
  const [onBreak, setOnBreak] = useState(() => {
    const saved = localStorage.getItem('onBreak');
    return saved === 'true';
  });
  const [currentJob, setCurrentJob] = useState(() => {
    const saved = localStorage.getItem('currentJob');
    return saved || null;
  });
  const [startTime, setStartTime] = useState(() => {
    const saved = localStorage.getItem('startTime');
    return saved ? new Date(parseInt(saved)) : null;
  });
  const [breakStartTime, setBreakStartTime] = useState(() => {
    const saved = localStorage.getItem('breakStartTime');
    return saved ? new Date(parseInt(saved)) : null;
  });
  const [notes, setNotes] = useState(() => {
    return localStorage.getItem('notes') || '';
  });
  const [timeEntries, setTimeEntries] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [startDateFilter, setStartDateFilter] = useState('');
  const [endDateFilter, setEndDateFilter] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [showClockModal, setShowClockModal] = useState(false);
  const [showClientModal, setShowClientModal] = useState(false);
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [internalUsers, setInternalUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingEntry, setEditingEntry] = useState(null);
  const [timerDisplay, setTimerDisplay] = useState('00:00:00');
  
  // Save state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('clockedIn', clockedIn);
    localStorage.setItem('onBreak', onBreak);
    localStorage.setItem('currentJob', currentJob);
    localStorage.setItem('startTime', startTime ? startTime.getTime() : null);
    localStorage.setItem('breakStartTime', breakStartTime ? breakStartTime.getTime() : null);
    localStorage.setItem('notes', notes);
  }, [clockedIn, onBreak, currentJob, startTime, breakStartTime, notes]);
  
  // Fetch accounts, users and time entries on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch accounts
        const accountsList = await getAccounts();
        setAccounts(accountsList);
        
        // Fetch internal users
        const usersList = await getInternalUsers();
        setInternalUsers(usersList);
        
        // Fetch time entries
        const entries = await getTimeEntries({
          startDate: startDateFilter,
          endDate: endDateFilter
        });
        setTimeEntries(entries);
      } catch (error) {
        console.error('Error fetching data:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [startDateFilter, endDateFilter]);
  
  // Format timer display
  const formatTimerDisplay = () => {
    if (!startTime) return "00:00:00";
    
    const now = new Date();
    let diffMs;
    
    if (onBreak) {
      // If on break, use the time when break started
      diffMs = breakStartTime - startTime;
    } else {
      diffMs = now - startTime;
    }
    
    const diffSec = Math.floor(diffMs / 1000);
    
    const hours = Math.floor(diffSec / 3600);
    const minutes = Math.floor((diffSec % 3600) / 60);
    const seconds = diffSec % 60;
    
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  // Update timer display every second
  useEffect(() => {
    if (!clockedIn) return;
    
    const interval = setInterval(() => {
      setTimerDisplay(formatTimerDisplay());
    }, 1000);
    
    return () => clearInterval(interval);
  }, [clockedIn, startTime, onBreak, breakStartTime]);
  
  // Handle clock in
  const handleClockIn = () => {
    if (!currentJob) {
      setShowClientModal(true);
      return;
    }
    
    const now = new Date();
    setStartTime(now);
    setClockedIn(true);
    setShowClockModal(false);
  };
  
  // Handle clock out
  const handleClockOut = async () => {
    try {
      console.log('Clock out initiated');
      console.log('Current state:', { startTime, currentJob, clockedIn, onBreak });

      if (!startTime || !currentJob) {
        console.error('Missing required data for clock out:', { startTime, currentJob });
        setError('Missing required data for clock out');
        return;
      }

      const now = new Date();
      const endTime = now;
      
      // Get selected account
      const selectedAccount = accounts.find(account => account.id === currentJob);
      console.log('Selected account:', selectedAccount);
      
      if (!selectedAccount) {
        console.error('No account found for ID:', currentJob);
        setError('No account selected');
        return;
      }
      
      // Create time entry data
      const timeEntryData = {
        accountId: selectedAccount.id,
        accountName: selectedAccount.company,
        clockIn: startTime,
        clockOut: endTime,
        notes: notes || '',
        hours: ((endTime - startTime) / (1000 * 60 * 60)).toFixed(2)
      };
      
      console.log('Creating time entry with data:', timeEntryData);
      
      // Save to Firestore
      const newEntry = await createTimeEntry(timeEntryData);
      console.log('Time entry created successfully:', newEntry);
      
      // Update local state
      setTimeEntries(prevEntries => [newEntry, ...prevEntries]);
      
      // Reset state and clear localStorage
      setClockedIn(false);
      setStartTime(null);
      setBreakStartTime(null);
      setNotes('');
      setCurrentJob(null);
      setOnBreak(false);
      setShowClockModal(false);
      setError(null);

      // Clear localStorage
      localStorage.removeItem('clockedIn');
      localStorage.removeItem('onBreak');
      localStorage.removeItem('currentJob');
      localStorage.removeItem('startTime');
      localStorage.removeItem('breakStartTime');
      localStorage.removeItem('notes');
      
      console.log('Clock out completed successfully');
    } catch (error) {
      console.error('Error in handleClockOut:', error);
      setError(error.message);
    }
  };
  
  // Handle break toggle
  const handleBreakToggle = () => {
    setOnBreak(!onBreak);
    if (!onBreak) {
      // Store the current time when break starts
      setBreakStartTime(new Date());
    } else {
      // Add the break duration to the start time when resuming
      const breakDuration = new Date() - breakStartTime;
      setStartTime(new Date(startTime.getTime() + breakDuration));
    }
    setShowClockModal(false);
  };
  
  // Handle manual time entry
  const handleManualEntry = async (formData) => {
    try {
      let clockIn, clockOut;
      
      // Calculate timestamps based on either direct input or times
      if (formData.totalHours) {
        const hours = parseFloat(formData.totalHours);
        if (isNaN(hours) || hours <= 0) {
          throw new Error('Please enter a valid number of hours');
        }
        clockIn = new Date(`${formData.date}T00:00:00`);
        clockOut = new Date(clockIn.getTime() + (hours * 3600000));
      } else if (formData.clockInTime && formData.clockOutTime) {
        clockIn = new Date(`${formData.date}T${formData.clockInTime}`);
        clockOut = new Date(`${formData.date}T${formData.clockOutTime}`);
        
        if (clockOut <= clockIn) {
          throw new Error('Clock out time must be after clock in time');
        }
      } else {
        throw new Error('Either hours or clock in/out times must be provided');
      }
      
      // Get selected account
      const selectedAccount = accounts.find(account => account.company === formData.client);
      if (!selectedAccount) {
        throw new Error('Please select a valid client');
      }
      
      // Create time entry data
      const timeEntryData = {
        accountId: selectedAccount.id,
        accountName: selectedAccount.company,
        clockIn,
        clockOut,
        notes: formData.notes || ''
      };
      
      // Save to Firestore
      const newEntry = await createTimeEntry(timeEntryData);
      
      // Update local state
      setTimeEntries(prevEntries => [newEntry, ...prevEntries]);
      setShowAddModal(false);
    } catch (error) {
      console.error('Error saving manual time entry:', error);
      throw error; // Re-throw to handle in the modal
    }
  };
  
  // Filter entries based on search and filters
  const filteredEntries = timeEntries.filter(entry => {
    const matchesSearch = 
      (entry.accountName?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (entry.userName?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (entry.notes?.toLowerCase() || '').includes(searchTerm.toLowerCase());
    
    // Date range filtering
    const entryDate = entry.clockIn;
    const startDateMatch = startDateFilter ? entryDate >= new Date(startDateFilter) : true;
    const endDateMatch = endDateFilter ? entryDate <= new Date(endDateFilter) : true;
    
    const matchesClientFilter = clientFilter ? entry.accountName === clientFilter : true;
    const matchesUserFilter = userFilter ? entry.userName === userFilter : true;
    
    return matchesSearch && startDateMatch && endDateMatch && matchesClientFilter && matchesUserFilter;
  });
  
  // Filter jobs based on search term
  const filteredJobs = accounts.filter(account => 
    (account.company?.toLowerCase() || '').includes(clientSearchTerm.toLowerCase())
  );
  
  // Handle delete time entry
  const handleDeleteEntry = async (entryId) => {
    if (window.confirm('Are you sure you want to delete this time entry?')) {
      try {
        await deleteTimeEntry(entryId);
        setTimeEntries(prevEntries => prevEntries.filter(entry => entry.id !== entryId));
      } catch (error) {
        console.error('Error deleting time entry:', error);
        setError(error.message);
      }
    }
  };

  // Handle edit time entry
  const handleEditEntry = (entry) => {
    setEditingEntry(entry);
    setShowAddModal(true);
  };
  
  // Manual Add Time Modal
  const AddTimeModal = ({ onSubmit, onClose, editingEntry }) => {
    // Format time to HH:mm for input
    const formatTimeForInput = (date) => {
      if (!date) return '';
      const d = new Date(date);
      return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    };

    const [formData, setFormData] = useState({
      date: editingEntry ? new Date(editingEntry.clockIn).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      clockInTime: editingEntry ? formatTimeForInput(editingEntry.clockIn) : '',
      clockOutTime: editingEntry ? formatTimeForInput(editingEntry.clockOut) : '',
      hours: '',
      minutes: '',
      totalHours: '',
      client: editingEntry ? editingEntry.accountName : (accounts[0]?.company || ''),
      notes: editingEntry ? editingEntry.notes : ''
    });
    
    const [entryMethod, setEntryMethod] = useState('times');
    const [timeError, setTimeError] = useState('');

    const handleChange = (e) => {
      const { name, value } = e.target;
      
      // Create new form data with the updated value
      const newFormData = {
        ...formData,
        [name]: value
      };
      
      // Auto-calculate hours when both clock in and clock out are provided
      if ((name === 'clockInTime' || name === 'clockOutTime')) {
        const clockIn = name === 'clockInTime' ? value : formData.clockInTime;
        const clockOut = name === 'clockOutTime' ? value : formData.clockOutTime;
        
        if (clockIn && clockOut) {
          try {
            // Create date objects using the selected date and times
            const [inHours, inMinutes] = clockIn.split(':').map(Number);
            const [outHours, outMinutes] = clockOut.split(':').map(Number);
            
            const startDateTime = new Date(formData.date);
            startDateTime.setHours(inHours, inMinutes, 0, 0);
            
            const endDateTime = new Date(formData.date);
            endDateTime.setHours(outHours, outMinutes, 0, 0);
            
            if (endDateTime > startDateTime) {
              const hours = (endDateTime - startDateTime) / (1000 * 60 * 60);
              newFormData.hours = hours.toFixed(2);
              setTimeError('');
            } else {
              setTimeError('Clock out time must be after clock in time');
            }
          } catch (error) {
            console.error('Time calculation error:', error);
            setTimeError('Invalid time format');
          }
        }
      }
      
      // Calculate total hours when hours or minutes change
      if (name === 'hours' || name === 'minutes') {
        const hours = parseFloat(newFormData.hours) || 0;
        const minutes = parseFloat(newFormData.minutes) || 0;
        newFormData.totalHours = (hours + (minutes / 60)).toFixed(2);
      }
      
      // Update the form data with all changes
      setFormData(newFormData);
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      
      try {
        const submissionData = { ...formData };
        
        if (entryMethod === 'hours') {
          submissionData.hours = submissionData.totalHours;
        }
        
        if (editingEntry) {
          // For editing, ensure we're using the correct date and times
          const [inHours, inMinutes] = submissionData.clockInTime.split(':').map(Number);
          const [outHours, outMinutes] = submissionData.clockOutTime.split(':').map(Number);
          
          const clockIn = new Date(submissionData.date);
          clockIn.setHours(inHours, inMinutes, 0, 0);
          
          const clockOut = new Date(submissionData.date);
          clockOut.setHours(outHours, outMinutes, 0, 0);
          
          // Calculate hours
          const hours = (clockOut - clockIn) / (1000 * 60 * 60);
          
          submissionData.clockIn = clockIn;
          submissionData.clockOut = clockOut;
          submissionData.hours = hours;
          
          await updateTimeEntry(editingEntry.id, submissionData);
          setTimeEntries(prevEntries => 
            prevEntries.map(entry => 
              entry.id === editingEntry.id 
                ? { 
                    ...entry,
                    clockIn,
                    clockOut,
                    hours,
                    notes: submissionData.notes,
                    accountName: submissionData.client
                  }
                : entry
            )
          );
        } else {
          await onSubmit(submissionData);
        }
        onClose();
      } catch (error) {
        console.error('Error submitting time entry:', error);
        setTimeError(error.message || 'Failed to submit time entry. Please try again.');
      }
    };
    
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
        <div className="bg-white dark:bg-gray-900 rounded-2xl w-full max-w-md p-6">
          <div className="flex justify-center items-center mb-4">
            <h2 className="text-xl font-bold dark:text-white">{editingEntry ? 'Edit Time Entry' : 'Add Time Entry'}</h2>
          </div>
          
          <div className="mb-4">
            <div className="flex border rounded overflow-hidden dark:border-gray-600">
              <button 
                type="button"
                className={`flex-1 py-2 px-4 ${entryMethod === 'times' ? 'bg-red-500 text-white' : 'bg-gray-100 dark:bg-gray-700 dark:text-white'}`}
                onClick={() => setEntryMethod('times')}
              >
                Clock In/Out
              </button>
              <button
                type="button"
                className={`flex-1 py-2 px-4 ${entryMethod === 'hours' ? 'bg-red-500 text-white' : 'bg-gray-100 dark:bg-gray-700 dark:text-white'}`}
                onClick={() => setEntryMethod('hours')}
              >
                Direct Hours
              </button>
            </div>
          </div>
          
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1 dark:text-gray-200">Date</label>
              <input 
                type="date" 
                name="date" 
                value={formData.date} 
                onChange={handleChange}
                className="w-full p-2 border rounded dark:bg-gray-700 dark:text-white dark:border-gray-600"
                required
              />
            </div>
            
            {entryMethod === 'times' ? (
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-1 dark:text-gray-200">Clock In</label>
                  <input 
                    type="time" 
                    name="clockInTime" 
                    value={formData.clockInTime} 
                    onChange={handleChange}
                    className="w-full p-2 border rounded dark:bg-gray-700 dark:text-white dark:border-gray-600"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1 dark:text-gray-200">Clock Out</label>
                  <input 
                    type="time" 
                    name="clockOutTime" 
                    value={formData.clockOutTime} 
                    onChange={handleChange}
                    className="w-full p-2 border rounded dark:bg-gray-700 dark:text-white dark:border-gray-600"
                    required
                  />
                  {timeError && (
                    <p className="text-red-500 text-sm mt-1">{timeError}</p>
                  )}
                </div>
                {formData.clockInTime && formData.clockOutTime && !timeError && (
                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-1 dark:text-gray-200">Calculated Hours</label>
                    <input 
                      type="number" 
                      name="calculatedHours"
                      value={formData.hours} 
                      readOnly
                      className="w-full p-2 border rounded bg-gray-50 dark:bg-gray-700 dark:text-white dark:border-gray-600"
                    />
                  </div>
                )}
              </div>
            ) : (
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1 dark:text-gray-200">Time</label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1 dark:text-gray-400">Hours</label>
                    <input 
                      type="number" 
                      name="hours" 
                      value={formData.hours} 
                      onChange={handleChange}
                      className="w-full p-2 border rounded dark:bg-gray-700 dark:text-white dark:border-gray-600"
                      min="0"
                      step="1"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1 dark:text-gray-400">Minutes</label>
                    <input 
                      type="number" 
                      name="minutes" 
                      value={formData.minutes} 
                      onChange={handleChange}
                      className="w-full p-2 border rounded dark:bg-gray-700 dark:text-white dark:border-gray-600"
                      min="0"
                      max="59"
                      step="1"
                      placeholder="0"
                    />
                  </div>
                </div>
                <div className="mt-2">
                  <label className="block text-sm font-medium mb-1 dark:text-gray-200">Total Hours</label>
                  <input 
                    type="number" 
                    name="totalHours"
                    value={formData.totalHours} 
                    readOnly
                    className="w-full p-2 border rounded bg-gray-50 dark:bg-gray-700 dark:text-white dark:border-gray-600"
                  />
                </div>
              </div>
            )}
            
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1 dark:text-gray-200">Client</label>
              <select 
                name="client" 
                value={formData.client} 
                onChange={handleChange}
                className="w-full p-2 border rounded dark:bg-gray-700 dark:text-white dark:border-gray-600"
                required
              >
                {accounts.map(account => (
                  <option key={account.id} value={account.company}>{account.company}</option>
                ))}
              </select>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1 dark:text-gray-200">Notes</label>
              <textarea 
                name="notes" 
                value={formData.notes} 
                onChange={handleChange}
                className="w-full p-2 border rounded dark:bg-gray-700 dark:text-white dark:border-gray-600"
                rows="3"
              />
            </div>
            
            <div className="flex justify-end space-x-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border rounded text-gray-700"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-red-500 text-white rounded"
              >
                {editingEntry ? 'Save Changes' : 'Add Entry'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };
  
  // Notes Modal
  const NotesModal = () => {
    const [localNotes, setLocalNotes] = useState(notes);

    const handleSave = () => {
      setNotes(localNotes);
      setShowNotesModal(false);
    };

    return (
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50" 
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            setShowNotesModal(false);
          }
        }}
      >
        <div className="bg-white dark:bg-gray-900 rounded-2xl w-full max-w-md p-6">
          <div className="flex items-center justify-center mb-4 relative">
            <h2 className="text-xl font-bold dark:text-white">Add Notes</h2>
            <button 
              onClick={() => setShowNotesModal(false)} 
              className="text-gray-500 absolute right-0"
            >
              <X size={20} />
            </button>
          </div>
          <textarea 
            value={localNotes} 
            onChange={(e) => setLocalNotes(e.target.value)}
            className="w-full p-2 border rounded dark:bg-gray-700 dark:text-white dark:border-gray-600"
            rows="6"
            placeholder="Type your notes here..."
          />
          <div className="flex justify-end space-x-2 mt-4">
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-red-500 text-white rounded"
            >
              Save Notes
            </button>
          </div>
        </div>
      </div>
    );
  };
  
  // Client Selection Modal
  const ClientModal = () => {
    const [localSearchTerm, setLocalSearchTerm] = useState(clientSearchTerm);
    const searchInputRef = useRef(null);

    // Focus the input when modal opens
    useEffect(() => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, []);

    const handleSearchChange = (e) => {
      const value = e.target.value;
      setLocalSearchTerm(value);
      setClientSearchTerm(value);
    };

    return (
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50" 
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            setShowClientModal(false);
          }
        }}
      >
        <div className="bg-white dark:bg-gray-900 rounded-2xl w-full max-w-md p-6">
          <div className="flex items-center justify-center mb-4 relative">
            <h2 className="text-xl font-bold dark:text-white">Select Client</h2>
            <button 
              onClick={() => setShowClientModal(false)} 
              className="text-gray-500 absolute right-0"
            >
              <X size={20} />
            </button>
          </div>
          
          <div className="relative mb-4">
            <input 
              ref={searchInputRef}
              type="text" 
              placeholder="Search clients..."
              value={localSearchTerm}
              onChange={handleSearchChange}
              className="w-full pl-10 pr-4 py-2 border rounded dark:bg-gray-700 dark:text-white dark:border-gray-600"
              list="client-search-options"
            />
            <datalist id="client-search-options">
              {accounts.map(account => (
                <option key={account.id} value={account.company}>{account.company}</option>
              ))}
            </datalist>
            <Search className="absolute left-3 top-2.5 text-gray-400" size={20} />
          </div>
          
          <div className="max-h-60 overflow-y-auto">
            {filteredJobs.map(account => (
              <div 
                key={account.id} 
                className="py-2 px-3 hover:bg-gray-100 cursor-pointer rounded"
                onClick={() => {
                  setCurrentJob(account.id);
                  setShowClientModal(false);
                  if (!clockedIn) {
                    setShowClockModal(true);
                  }
                }}
              >
                {account.company || 'Unnamed Account'}
              </div>
            ))}
            {filteredJobs.length === 0 && (
              <div className="py-4 text-center text-gray-500">
                No accounts found
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };
  
  // Show client name if selected
  const selectedClientName = currentJob ? accounts.find(account => account.id === currentJob)?.company : null;
  
  return (
    <div className="container mx-auto p-4 max-w-6xl relative z-10 min-h-screen">
      <PortalBackground />
      <div className="relative">
      <div className="flex justify-center items-center mb-6 relative">
        <button
          className="absolute left-0 ml-24 hover:opacity-80 transition-opacity duration-200"
          title="Time Tracking"
        >
          <img 
            src="/agent-timer.svg" 
            alt="Time Tracking" 
            className="w-24 h-24 object-contain"
          />
        </button>
        <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">Time Tracking</h1>
        
        <div className="absolute right-0 flex items-center space-x-3">
          {clockedIn && (
            <div className="flex items-center mr-4">
              <div className={`w-3 h-3 rounded-full ${onBreak ? 'bg-yellow-500' : 'bg-red-500'} mr-2 animate-pulse`}></div>
              <span className="text-sm font-medium dark:text-white">
                {selectedClientName} · {timerDisplay}
              </span>
            </div>
          )}
          
          <button 
            className="text-sm flex items-center justify-center bg-red-500 text-white px-3 py-1.5 rounded"
            onClick={() => setShowAddModal(true)}
          >
            <PlusCircle size={16} className="mr-1" />
            Add New Time
          </button>
        </div>
      </div>
      
      {/* Time Entries Dashboard */}
      <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm">
        
        {/* Filters and Search */}
        <div className="p-4 border-t border-b dark:border-gray-700">
          <div className="flex items-center space-x-4">
            {/* Search */}
            <div className="relative w-64">
              <input 
                type="text" 
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded dark:bg-gray-700 dark:text-white dark:border-gray-600"
              />
              <Search className="absolute left-3 top-2.5 text-gray-400" size={20} />
            </div>
            
            {/* Date Range Filter */}
            <div className="flex items-center space-x-2">
              <Calendar className="text-gray-400" size={20} />
              <input 
                type="date" 
                placeholder="From"
                value={startDateFilter}
                onChange={(e) => setStartDateFilter(e.target.value)}
                className="py-2 px-3 border rounded dark:bg-gray-700 dark:text-white dark:border-gray-600"
              />
              <span className="text-gray-400">to</span>
              <input 
                type="date" 
                placeholder="To"
                value={endDateFilter}
                onChange={(e) => setEndDateFilter(e.target.value)}
                className="py-2 px-3 border rounded dark:bg-gray-700 dark:text-white dark:border-gray-600"
              />
            </div>
            
            {/* Client Filter */}
            <div className="relative">
              <div className="relative inline-block w-64">
                <div className="flex items-center border rounded">
                  <Briefcase className="mx-2 text-gray-400" size={20} />
                  <input
                    type="text"
                    placeholder="All Clients"
                    value={clientFilter}
                    onChange={(e) => setClientFilter(e.target.value)}
                    className="py-2 pr-8 pl-1 w-full outline-none dark:bg-gray-700 dark:text-white dark:border-gray-600"
                    onClick={() => document.getElementById('client-dropdown').classList.toggle('hidden')}
                  />
                  <div className="absolute right-0 top-0 h-full flex items-center pr-2">
                    <ChevronDown size={16} className="text-gray-400" />
                  </div>
                </div>
                <div id="client-dropdown" className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border dark:border-gray-600 rounded shadow-lg hidden max-h-60 overflow-y-auto">
                  <div className="p-2">
                    <label className="flex items-center px-2 py-1 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={clientFilter === ''}
                        onChange={() => setClientFilter('')}
                        className="mr-2" 
                      />
                      <span>All Clients</span>
                    </label>
                    {accounts.filter(account => 
                      (account.company?.toLowerCase() || '').includes(clientFilter.toLowerCase())
                    ).map(account => (
                      <label key={account.id} className="flex items-center px-2 py-1 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer">
                        <input 
                          type="checkbox" 
                          checked={clientFilter === account.company}
                          onChange={() => setClientFilter(account.company)}
                          className="mr-2" 
                        />
                        <span>{account.company || 'Unnamed Account'}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            
            {/* User Filter */}
            <div className="relative">
              <div className="relative inline-block w-64">
                <div className="flex items-center border rounded">
                  <Users className="mx-2 text-gray-400" size={20} />
                  <input
                    type="text"
                    placeholder="All Users"
                    value={userFilter}
                    onChange={(e) => setUserFilter(e.target.value)}
                    className="py-2 pr-8 pl-1 w-full outline-none dark:bg-gray-700 dark:text-white dark:border-gray-600"
                    onClick={() => document.getElementById('user-dropdown').classList.toggle('hidden')}
                  />
                  <div className="absolute right-0 top-0 h-full flex items-center pr-2">
                    <ChevronDown size={16} className="text-gray-400" />
                  </div>
                </div>
                <div id="user-dropdown" className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border dark:border-gray-600 rounded shadow-lg hidden max-h-60 overflow-y-auto">
                  <div className="p-2">
                    <label className="flex items-center px-2 py-1 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={userFilter === ''}
                        onChange={() => setUserFilter('')}
                        className="mr-2" 
                      />
                      <span>All Users</span>
                    </label>
                    {internalUsers
                      .filter(user => user.name.toLowerCase().includes(userFilter.toLowerCase()))
                      .map(user => (
                        <label key={user.id} className="flex items-center px-2 py-1 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer">
                          <input 
                            type="checkbox" 
                            checked={userFilter === user.name}
                            onChange={() => setUserFilter(user.name)}
                            className="mr-2" 
                          />
                          <span>{user.name}</span>
                        </label>
                      ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Time Entries Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-700">
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-300">Date</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-300">Clock In</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-300">Clock Out</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-300">Hours</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-300">Account</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-300">User</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-300">Notes</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-300">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y dark:divide-gray-700">
              {filteredEntries.map(entry => (
                <tr key={entry.id} className="dark:bg-gray-800">
                  <td className="px-4 py-3 text-sm dark:text-gray-100">{entry.clockIn.toLocaleDateString()}</td>
                  <td className="px-4 py-3 text-sm dark:text-gray-100">{entry.clockIn.toLocaleTimeString()}</td>
                  <td className="px-4 py-3 text-sm dark:text-gray-100">{entry.clockOut.toLocaleTimeString()}</td>
                  <td className="px-4 py-3 text-sm dark:text-gray-100">{entry.hours.toFixed(2)}</td>
                  <td className="px-4 py-3 text-sm dark:text-gray-100">{entry.accountName}</td>
                  <td className="px-4 py-3 text-sm dark:text-gray-100">{entry.userName}</td>
                  <td className="px-4 py-3 text-sm dark:text-gray-100">
                    {entry.notes.length > 20 ? `${entry.notes.substring(0, 20)}...` : entry.notes}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <div className="flex space-x-2">
                      <button 
                        className="text-gray-500 hover:text-blue-500 dark:text-gray-300 dark:hover:text-blue-400"
                        onClick={() => handleEditEntry(entry)}
                      >
                        <Edit size={16} />
                      </button>
                      <button 
                        className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-600"
                        onClick={() => handleDeleteEntry(entry.id)}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredEntries.length === 0 && (
                <tr>
                  <td colSpan="8" className="px-4 py-8 text-center text-gray-500">
                    {loading ? 'Loading...' : 'No time entries found'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination (placeholder) */}
        <div className="p-4 border-t flex justify-between items-center">
          <div className="text-sm text-gray-500">
            Showing {filteredEntries.length} of {timeEntries.length} entries
          </div>
          <div className="flex space-x-2">
            <button className="px-3 py-1 border rounded">Previous</button>
            <button className="px-3 py-1 bg-red-50 border border-red-500 rounded">1</button>
            <button className="px-3 py-1 border rounded">2</button>
            <button className="px-3 py-1 border rounded">Next</button>
          </div>
        </div>
      </div>
      
      {/* Floating Clock Button */}
      <div className="fixed bottom-6 right-6 z-40">
        <button 
          className={`w-12 h-12 rounded-full ${clockedIn ? (onBreak ? 'bg-yellow-500' : 'bg-red-500') : 'bg-red-500'} flex items-center justify-center text-white shadow-lg`}
          onClick={() => setShowClockModal(true)}
        >
          <Clock size={24} />
        </button>
      </div>
      
      {/* Modals */}
      {showAddModal && (
        <AddTimeModal 
          onSubmit={handleManualEntry} 
          onClose={() => {
            setShowAddModal(false);
            setEditingEntry(null);
          }}
          editingEntry={editingEntry}
        />
      )}
      {showNotesModal && <NotesModal />}
      {showClientModal && <ClientModal />}
      {showClockModal && (
        <div className="fixed bottom-0 right-0 top-0 w-72 bg-white dark:bg-gray-800 shadow-lg z-40 flex flex-col">
          <div className="p-4 border-b dark:border-gray-700">
            {clockedIn && selectedClientName && (
              <div className="flex items-center">
                <div className={`w-2 h-2 rounded-full ${onBreak ? 'bg-yellow-500' : 'bg-green-500'} mr-2`}></div>
                <span className="text-sm font-medium dark:text-white">{selectedClientName} · {timerDisplay}</span>
              </div>
            )}
          </div>
          <div className="flex-1 flex flex-col items-center justify-center p-6">
            <div className="text-center mb-2">
              <div className="text-sm text-gray-600 dark:text-gray-300">
                Today · {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </div>
            </div>
            <div className="text-3xl font-light mb-8 dark:text-white">
              {timerDisplay}
            </div>
            
            {clockedIn ? (
              onBreak ? (
                <button 
                  type="button"
                  className="w-40 h-40 rounded-full bg-yellow-500 flex items-center justify-center text-white font-bold text-xl cursor-pointer shadow-lg mb-8 hover:bg-yellow-600 transition-colors"
                  onClick={handleBreakToggle}
                >
                  RESUME
                </button>
              ) : (
                <button 
                  type="button"
                  className="w-40 h-40 rounded-full bg-red-500 flex items-center justify-center text-white font-bold text-xl cursor-pointer shadow-lg mb-8 hover:bg-red-600 transition-colors"
                  onClick={handleClockOut}
                >
                  CLOCK OUT
                </button>
              )
            ) : (
              <button 
                type="button"
                className="w-40 h-40 rounded-full bg-green-500 flex items-center justify-center text-white font-bold text-xl cursor-pointer shadow-lg mb-8 hover:bg-green-600 transition-colors"
                onClick={handleClockIn}
              >
                CLOCK IN
              </button>
            )}
            
            <div className="grid grid-cols-3 gap-6 w-full">
              <button 
                type="button"
                className="flex flex-col items-center"
                onClick={() => setShowNotesModal(true)}
              >
                <div className="w-12 h-12 rounded-full border dark:border-gray-600 flex items-center justify-center mb-1 dark:bg-gray-700">
                  <Clock size={20} />
                </div>
                <span className="text-xs dark:text-gray-200">Add Notes</span>
              </button>
              
              {clockedIn && !onBreak ? (
                <button 
                  type="button"
                  className="flex flex-col items-center"
                  onClick={handleBreakToggle}
                >
                  <div className="w-12 h-12 rounded-full border dark:border-gray-600 flex items-center justify-center mb-1 dark:bg-gray-700">
                    <Pause size={20} />
                  </div>
                  <span className="text-xs dark:text-gray-200">Take Break</span>
                </button>
              ) : clockedIn && onBreak ? (
                <button 
                  type="button"
                  className="flex flex-col items-center"
                  onClick={handleBreakToggle}
                >
                  <div className="w-12 h-12 rounded-full border dark:border-gray-600 flex items-center justify-center mb-1 dark:bg-gray-700">
                    <Play size={20} />
                  </div>
                  <span className="text-xs dark:text-gray-200">Resume</span>
                </button>
              ) : (
                <button 
                  type="button"
                  className="flex flex-col items-center opacity-50"
                  disabled
                >
                  <div className="w-12 h-12 rounded-full border dark:border-gray-600 flex items-center justify-center mb-1 dark:bg-gray-700">
                    <Pause size={20} />
                  </div>
                  <span className="text-xs dark:text-gray-200">Take Break</span>
                </button>
              )}
              
              <button 
                type="button"
                className="flex flex-col items-center"
                onClick={() => setShowClientModal(true)}
              >
                <div className="w-12 h-12 rounded-full border dark:border-gray-600 flex items-center justify-center mb-1 dark:bg-gray-700">
                  <Briefcase size={20} />
                </div>
                <span className="text-xs dark:text-gray-200">{clockedIn ? 'Switch Job' : 'Add Client'}</span>
              </button>
            </div>
          </div>
          
          <div className="p-4 border-t dark:border-gray-700">
            <button
              type="button"
              className="w-full py-2 text-sm text-gray-600 dark:text-gray-300 flex justify-center items-center"
              onClick={() => setShowClockModal(false)}
            >
              <X size={16} className="mr-1" /> Close
            </button>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default TimeTracking; 